/**
 * Custom hook for generating template previews
 * Handles preview generation for database, translation keys, types, tags, dialogs, and variables
 */

import { useState } from 'react';
import { ExportTemplate, NamingConvention } from '@/types/exportTemplates';
import { renderFolderBasedDatabaseTemplate } from '@/lib/rerun-manager/templateEngine';
import { getFolderPath } from '@/lib/stores/folderStore';

interface UseTemplatePreviewProps {
  selectedGame: any;
  allItems: any[];
  glossaryBlueprints: any[];
  types: any[];
  tags: any[];
}

export const useTemplatePreview = ({
  selectedGame,
  allItems,
  glossaryBlueprints,
  types,
  tags
}: UseTemplatePreviewProps) => {
  const [previewContent, setPreviewContent] = useState<string>('');
  const [translationKeysPreview, setTranslationKeysPreview] = useState<string>('');
  const [typesPreview, setTypesPreview] = useState<string>('');
  const [tagsPreview, setTagsPreview] = useState<string>('');
  const [dialogDatabasePreview, setDialogDatabasePreview] = useState<string>('');
  const [dialogVariablesPreview, setDialogVariablesPreview] = useState<string>('');
  const [previewFileType, setPreviewFileType] = useState<'database' | 'translations' | 'types' | 'tags' | 'dialogs' | 'variables'>('database');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewNamingConvention, setPreviewNamingConvention] = useState<NamingConvention>('original');

  // Generate preview for database templates
  const generatePreview = async (template: ExportTemplate) => {
    if (!selectedGame) return;

    setIsGeneratingPreview(true);

    try {
      // Create folder path resolver
      const folderPathResolver = (folderId: string | null | undefined): string => {
        if (!folderId) return '';
        return getFolderPath(selectedGame.id, 'library', folderId);
      };

      // Get items to preview (limit to first 10 for performance)
      const previewItems = (allItems || []).slice(0, 10);

      // Fetch dialogs for preview (limit to first 5 for performance)
      let previewDialogs: any[] = [];
      try {
        const { apiService } = await import('../../../lib/api-functions/api-service');
        const dialogsResult = await apiService.fetchDialogs(selectedGame.id);
        if (dialogsResult.success && dialogsResult.data) {
          previewDialogs = dialogsResult.data.slice(0, 5);
          console.log(`📝 Fetched ${previewDialogs.length} dialogs for preview`);
        }
      } catch (error) {
        console.warn('Failed to fetch dialogs for preview, continuing without them:', error);
      }

      // Import strings store and get translation strings
      import('../../../lib/stores/stringsStore').then(({ getCachedStringsItems }) => {
        const stringsItems = getCachedStringsItems(selectedGame.id);

        // Generate preview content with translation support
        const content = renderFolderBasedDatabaseTemplate(
          template,
          previewItems,
          glossaryBlueprints || [],
          folderPathResolver,
          types,
          tags,
          allItems,
          previewNamingConvention,
          template.databaseSettings?.namespace || 'Grimoire.Data',
          template.databaseSettings?.className || 'Database',
          stringsItems,
          previewDialogs
        );

        setPreviewContent(content);

        // Generate translation keys preview if applicable
        if (template.platform === 'unity' && stringsItems.length > 0) {
          import('../../../lib/integrations/templateEngine').then(({ renderTranslationKeysFile }) => {
            const namespace = template.databaseSettings?.namespace || 'Grimoire.Data';
            const translationKeysClassName = template.databaseSettings?.translationKeysClassName || 'TranslationKeys';
            const gameLanguages = selectedGame.settings?.languages || ['en'];
            const translationKeysContent = renderTranslationKeysFile(stringsItems, namespace, translationKeysClassName, gameLanguages);
            setTranslationKeysPreview(translationKeysContent);
          }).catch((err) => {
            console.error('Failed to generate translation keys preview:', err);
            setTranslationKeysPreview('// Error generating translation keys preview');
          });
        } else {
          setTranslationKeysPreview('');
        }

        // Generate Types.cs preview if applicable
        if (template.platform === 'unity' && types && types.length > 0) {
          import('../../../lib/integrations/templateEngine').then(({ renderTypesFile }) => {
            const namespace = template.databaseSettings?.namespace || 'Grimoire.Data';
            const typesContent = renderTypesFile(types, namespace, 'Types');
            setTypesPreview(typesContent);
          }).catch((err) => {
            console.error('Failed to generate types preview:', err);
            setTypesPreview('// Error generating types preview');
          });
        } else {
          setTypesPreview('');
        }

        // Generate Tags.cs preview if applicable
        if (template.platform === 'unity' && tags && tags.length > 0) {
          import('../../../lib/integrations/templateEngine').then(({ renderTagsFile }) => {
            const namespace = template.databaseSettings?.namespace || 'Grimoire.Data';
            const tagsContent = renderTagsFile(tags, namespace, 'Tags');
            setTagsPreview(tagsContent);
          }).catch((err) => {
            console.error('Failed to generate tags preview:', err);
            setTagsPreview('// Error generating tags preview');
          });
        } else {
          setTagsPreview('');
        }

        // Generate DialogDatabase.cs preview if applicable
        if (template.platform === 'unity' && previewDialogs.length > 0) {
          import('../../../lib/integrations/templateEngine').then(({ renderDialogDatabaseFile }) => {
            const namespace = template.databaseSettings?.namespace || 'Grimoire.Data';
            const dialogDatabaseClassName = template.databaseSettings?.dialogDatabaseClassName || 'DialogDatabase';
            const fieldConvention = previewNamingConvention || template.namingConventions.fieldName;
            
            // Build strings map
            const stringsMap = new Map<string, any>();
            stringsItems.forEach((item: any) => {
              stringsMap.set(item.id, item);
            });
            
            const dialogDatabaseContent = renderDialogDatabaseFile(
              previewDialogs,
              folderPathResolver,
              stringsMap,
              fieldConvention,
              namespace,
              dialogDatabaseClassName
            );
            setDialogDatabasePreview(dialogDatabaseContent || '// No dialogs to preview');
          }).catch((err) => {
            console.error('Failed to generate dialog database preview:', err);
            setDialogDatabasePreview('// Error generating dialog database preview');
          });
        } else {
          setDialogDatabasePreview('');
        }

        // Generate DialogVariables.cs preview if applicable
        if (template.platform === 'unity' && previewDialogs.length > 0) {
          import('../../../lib/integrations/templateEngine').then(({ renderDialogVariablesFile }) => {
            const namespace = template.databaseSettings?.namespace || 'Grimoire.Data';
            const dialogVariablesClassName = template.databaseSettings?.dialogVariablesClassName || 'DialogVariables';
            
            const dialogVariablesContent = renderDialogVariablesFile(
              previewDialogs,
              namespace,
              dialogVariablesClassName
            );
            setDialogVariablesPreview(dialogVariablesContent || '// No dialog variables to preview');
          }).catch((err) => {
            console.error('Failed to generate dialog variables preview:', err);
            setDialogVariablesPreview('// Error generating dialog variables preview');
          });
        } else {
          setDialogVariablesPreview('');
        }

        setIsGeneratingPreview(false);
      }).catch((error) => {
        console.error('Failed to load strings:', error);
        // Fallback: generate without strings
        const content = renderFolderBasedDatabaseTemplate(
          template,
          previewItems,
          glossaryBlueprints || [],
          folderPathResolver,
          types,
          tags,
          allItems,
          previewNamingConvention,
          template.databaseSettings?.namespace || 'Grimoire.Data',
          template.databaseSettings?.className || 'Database'
        );
        setPreviewContent(content);
        setIsGeneratingPreview(false);
      });

      return; // Early return since we're using async
    } catch (error) {
      console.error('Preview generation failed:', error);
      setPreviewContent(`// Error generating preview:\n// ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsGeneratingPreview(false);
    }
  };

  return {
    previewContent,
    translationKeysPreview,
    typesPreview,
    tagsPreview,
    dialogDatabasePreview,
    dialogVariablesPreview,
    previewFileType,
    setPreviewFileType,
    isGeneratingPreview,
    previewNamingConvention,
    setPreviewNamingConvention,
    generatePreview
  };
};
